import { useQuery } from '@tanstack/react-query';

import { isNetworkError } from '@/lib/api';
import { useQueue } from '@/lib/queue';
import { readShiftCache } from '@/lib/shift-cache';
import { getPoint, type PointScreen } from '@/services/courier';

const DEFAULT_RADIUS_M = 300;

/** Экран точки из того, что уже есть в кэше смены, — когда сервер недоступен. */
function fromShiftCache(pointId: string): PointScreen | null {
  const active = readShiftCache();
  const p = active?.points.find((x) => x.id === pointId);
  if (!active || !p) return null;

  // В машине по накладной минус то, что уже отдали на точках, ждущих отправки.
  const spent = new Map<string, number>();
  for (const q of useQueue.getState().items) {
    if (q.kind !== 'close' || q.shiftId !== active.shift.id || q.pointId === pointId) continue;
    for (const l of q.body.lines) spent.set(l.itemId, (spent.get(l.itemId) ?? 0) + l.delivered);
    for (const s of q.body.swaps ?? []) spent.set(s.toItemId, (spent.get(s.toItemId) ?? 0) + s.toQty);
  }
  const priceOf = new Map(active.menu.map((m) => [m.id, m.price]));
  const beyond = active.settings?.sellBeyondLoad === true;
  const fromLoad = active.load
    ? active.load.lines
        .filter((l) => l.issued > 0 && priceOf.has(l.itemId))
        .map((l) => ({
          id: l.itemId,
          name: l.name,
          unit: l.unit,
          price: priceOf.get(l.itemId)!,
          inCar: Math.max(0, Math.round((l.remaining - (spent.get(l.itemId) ?? 0)) * 1000) / 1000),
          returnable: -1,
        }))
    : null;
  const menu: PointScreen['menu'] = active.load
    ? beyond
      ? active.menu.map((m) => ({
          ...m,
          inCar: fromLoad?.find((l) => l.id === m.id)?.inCar ?? 0,
          returnable: -1,
        }))
      : (fromLoad ?? [])
    : active.menu.map((m) => ({ ...m, inCar: null, returnable: -1 }));

  return {
    shift: { id: active.shift.id, routeName: active.shift.routeName, cityName: active.shift.cityName, loadNumber: active.load?.number ?? null },
    point: { id: p.id, name: p.name, address: p.address, phone: p.phone, comment: p.comment, debt: p.debt, lat: p.lat, lon: p.lon },
    radiusM: DEFAULT_RADIUS_M,
    photoRequired: active.settings?.shelfPhoto ?? true,
    gpsRequired: active.settings?.shipWithoutGps !== true,
    sellBeyondLoad: active.settings?.sellBeyondLoad === true,
    hours: p.hours ?? null,
    payMode: p.payMode ?? 'FREE',
    payMethods: p.payMethods,
    debtLimit: p.debtLimit ?? null,
    visit: null,
    menu,
    // Без сервера не знаем, что точка может вернуть, — проверит сервер при отправке.
    returnables: [],
    recent: [],
  };
}

export function usePointScreen(pointId: string) {
  return useQuery({
    queryKey: ['point', pointId],
    queryFn: async () => {
      try {
        return { data: await getPoint(pointId), offline: false };
      } catch (err) {
        const cached = isNetworkError(err) ? fromShiftCache(pointId) : null;
        if (cached) return { data: cached, offline: true };
        throw err;
      }
    },
    retry: false,
  });
}
